import { createPortal } from "react-dom";
import { TooltipCard } from "../../atoms/Tooltip";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { computeTooltipCoords } from "./computeTooltipCoords";

const PORTAL = document.getElementById("root-modals") || document.body;

/**
 * Tooltip
 *
 * Molecule that renders a smart, portal‑based tooltip:
 *   • Positions itself below the `anchorEl` (or above if there’s no room).
 *   • Keeps the entire panel inside the viewport, adding a 12 px gutter.
 *   • Slides the arrow horizontally so it points at the anchor, but never
 *     closer than 12 px from either edge of the tooltip.
 *   • Uses `position: fixed` + viewport coords so a positioned `#root-modals`
 *     portal does not shift the tip off-screen or grow page scrollbars.
 *   • Recomputes on mount, on anchor changes, and on scroll / resize.
 *   • Cleans up its event listeners automatically on unmount.
 *
 * Params
 * - **anchorEl** (HTMLElement) — DOM element the tooltip should point to.
 * - **text** (string | ReactNode) — content shown inside the tooltip shell.
 * - ...props (object): Any additional props are forwarded to
 *   `TooltipCard` (e.g., `style`, `onClick`, `data-*` attributes).
 *
 * Internal state
 * - **coords** { top, left, placement, arrowLeft } — calculated geometry
 *   passed down to `TooltipCard` via inline styles and CSS variable
 *   `--arrow-left`.
 *
 * Returns
 * - React portal containing `<TooltipCard …>` positioned at `coords`.
 *
 *
 * The companion **EllipsisText** molecule handles the “only show tooltip if
 * text is truncated” logic and passes its own span element as `anchorEl`.
 */
export function Tooltip({ anchorEl, text, ...props }) {
  const tipRef = useRef(null);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    placement: "bottom",
    arrowLeft: "50%",
  });

  // main positioning fn
  const update = useCallback(() => {
    if (!anchorEl || !tipRef.current) return;

    setCoords(
      computeTooltipCoords({
        anchorRect: anchorEl.getBoundingClientRect(),
        tipW: tipRef.current.offsetWidth,
        tipH: tipRef.current.offsetHeight,
        vpW: window.innerWidth,
        vpH: window.innerHeight,
      }),
    );
  }, [anchorEl]);

  /* recalc on mount & anchor change */
  useLayoutEffect(update, [update]);

  /* recalc on scroll / resize */
  useEffect(() => {
    window.addEventListener("scroll", update, true); // capture
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  if (!anchorEl) return null;

  return createPortal(
    <TooltipCard
      {...props}
      ref={tipRef}
      placement={coords.placement}
      style={{
        top: coords.top,
        left: coords.left,
        "--arrow-left": coords.arrowLeft,
      }}
    >
      {text}
    </TooltipCard>,
    PORTAL,
  );
}
